import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { format, subDays, eachDayOfInterval, eachHourOfInterval, differenceInHours } from "date-fns";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Activity,
  Wallet,
  Calendar,
  RefreshCw,
  Users,
  Server,
} from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import EngineStatCard from "@/shared/components/engine/EngineStatCard";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import { getDateRange, filterTransactionsByDateRange } from "@/shared/utils/dateFilters";
import * as engineService from "@/shared/api/services/engineService";
import * as transactionService from "@/shared/api/services/transactionService";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";

export default function EngineDashboard() {
  const { t } = useLanguage();
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });

  // Fetch Profile to get reseller name
  const { data: profile } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
  });

  const resellerName = profile?.name || "Engine User";

  // Fetch Engine's merchants to scope transactions
  const { data: engineMerchants = [], isLoading: merchantsLoading } = useQuery({
    queryKey: ["engine-merchants"],
    queryFn: () => engineService.getMyMerchants(),
  });

  const merchantIds = useMemo(() => engineMerchants.map(m => m.id), [engineMerchants]);

  // Fetch Dashboard-specific stats (Active Merchants, etc.)
  const { data: dashboardStats } = useQuery({
    queryKey: ["engine-dashboard-stats"],
    queryFn: () => engineService.getDashboardStats(),
  });

  // Fetch Transactions for chart and recent list
  const { data: paginationResult, isLoading: transactionsLoading } = useQuery({
    queryKey: ["engine-dashboard-transactions", merchantIds],
    queryFn: () => {
      if (merchantIds.length === 0) return { data: [] };
      // Fetch a larger set for the chart and metrics
      return transactionService.getPaginatedTransactions(1, 500, "", {}, merchantIds);
    },
    enabled: merchantIds.length > 0,
  });

  const transactions = useMemo(() => {
    return mapGlobalTransactions(paginationResult?.data || []);
  }, [paginationResult]);

  // Get date range based on filter
  const dateRange = useMemo(() => {
    return getDateRange(dateFilter, customDateRange);
  }, [dateFilter, customDateRange]);

  // Filter transactions by date range
  const filteredTransactions = useMemo(() => {
    return filterTransactionsByDateRange(
      transactions,
      dateRange.startDate,
      dateRange.endDate
    );
  }, [transactions, dateRange]);

  // Calculate stats based on filtered transactions
  const totalVolume = useMemo(() => 
    filteredTransactions
      .filter(t => t.status === "success")
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
  , [filteredTransactions]);

  const successDeposits = useMemo(() => 
    filteredTransactions.filter(t => t.type === "deposit" && t.status === "success").length
  , [filteredTransactions]);

  const failedDeposits = useMemo(() => 
    filteredTransactions.filter(t => t.type === "deposit" && (t.status === "failed" || t.status === "declined")).length
  , [filteredTransactions]);

  const totalWithdrawals = useMemo(() => 
    filteredTransactions
      .filter(t => t.type === "withdrawal" && t.status === "success")
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
  , [filteredTransactions]);

  const activeMerchants = dashboardStats?.activeMerchants ?? engineMerchants.filter(m => m.status === "active").length;

  // Fetch resellers for the stat card
  const { data: resellers = [], isLoading: resellersLoading } = useQuery({
    queryKey: ["engine-resellers"],
    queryFn: () => engineService.getMyResellers(),
  });
  const activeResellers = resellers.filter(r => r.status === "active").length;

  // Generate chart data based on date range
  const chartData = useMemo(() => {
    const { startDate, endDate } = dateRange;
    const hoursDiff = differenceInHours(endDate, startDate);

    if (hoursDiff <= 48) {
      const hours = eachHourOfInterval({ start: startDate, end: endDate });
      return hours.map((hour) => {
        const hourTxs = filteredTransactions.filter((t) => {
          const txDate = new Date(t.createdAt);
          return txDate >= hour && txDate < new Date(hour.getTime() + 60 * 60 * 1000) && t.status === "success";
        });
        const deposits = hourTxs.filter(t => t.type === "deposit").reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
        const withdrawals = hourTxs.filter(t => t.type === "withdrawal").reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
        return {
          date: format(hour, "HH:mm"),
          deposits: deposits / 1000,
          withdrawals: withdrawals / 1000,
        };
      });
    } else {
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      return days.map((day) => {
        const dayTxs = filteredTransactions.filter(t => {
          const txDate = new Date(t.createdAt);
          return txDate >= day && txDate < new Date(day.getTime() + 24 * 60 * 60 * 1000) && t.status === "success";
        });
        const deposits = dayTxs.filter(t => t.type === "deposit").reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
        const withdrawals = dayTxs.filter(t => t.type === "withdrawal").reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
        return {
          date: format(day, "MMM d"),
          deposits: deposits / 1000,
          withdrawals: withdrawals / 1000,
        };
      });
    }
  }, [filteredTransactions, dateRange]);

  const recentTransactions = filteredTransactions.slice(0, 10);

  const isLoading = merchantsLoading || transactionsLoading || resellersLoading;

  return (
    <EngineLayout currentPage="EngineDashboard" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("dashboard")}</h1>
            <p className="text-slate-500">{t("welcomeBack")}, {resellerName}</p>
          </div>
          <div className="flex items-center gap-3">
            <DateFilter
              value={dateFilter}
              onChange={setDateFilter}
              customDateRange={customDateRange}
              onCustomDateRangeChange={setCustomDateRange}
            />
            <Button variant="outline" size="icon" className="bg-white" onClick={() => window.location.reload()}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <EngineStatCard
            title={t("totalVolume")}
            value={`RS${totalVolume.toLocaleString()}`}
            trend="+12.5%"
            trendType="positive"
            icon={Wallet}
            iconColor="bg-sky-100 text-sky-600"
            delay={0}
          />
          <EngineStatCard
            title={t("deposits")}
            value={successDeposits}
            icon={CheckCircle2}
            iconColor="bg-emerald-100 text-emerald-600"
            delay={0.05}
          />
          <EngineStatCard
            title={t("failed")}
            value={failedDeposits}
            icon={XCircle}
            iconColor="bg-red-100 text-red-600"
            delay={0.1}
          />
          <EngineStatCard
            title={t("withdrawals")}
            value={`RS${totalWithdrawals.toLocaleString()}`}
            icon={ArrowUpFromLine}
            iconColor="bg-purple-100 text-purple-600"
            delay={0.15}
          />
          <EngineStatCard
            title={t("merchants")}
            value={activeMerchants}
            icon={Building2}
            iconColor="bg-amber-100 text-amber-600"
            delay={0.2}
          />
          <EngineStatCard
            title={t("resellers")}
            value={activeResellers}
            icon={Users}
            iconColor="bg-pink-100 text-pink-600"
            delay={0.25}
          />
        </div>

        {/* Chart & Recent Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Volume Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{t("volumeOverviewTitle")}</h3>
                <p className="text-sm text-slate-500">{t("transactionVolume")}</p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-sky-500" />
                  <span className="text-slate-600">{t("deposits")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-slate-600">{t("withdrawals")}</span>
                </div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="depositsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="withdrawalsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `${v}K`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value) => [`RS${(value * 1000).toLocaleString()}`, ""]}
                  />
                  <Area
                    type="monotone"
                    dataKey="deposits"
                    stroke="#0ea5e9"
                    strokeWidth={2}
                    fill="url(#depositsGradient)"
                  />
                  <Area
                    type="monotone"
                    dataKey="withdrawals"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#withdrawalsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Recent Transactions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm border border-slate-100"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{t("recent")}</h3>
              <Link to={createPageUrl("EngineTransactions")} className="text-sm text-sky-600 hover:text-sky-700">
                {t("viewAll")}
              </Link>
            </div>
            <div className="space-y-2">
              {isLoading && recentTransactions.length === 0 ? (
                <div className="space-y-3 py-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-12 w-full bg-slate-50 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : recentTransactions.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{t("noTransactionsYet")}</p>
              ) : (
                recentTransactions.map((tx, index) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35 + index * 0.03 }}
                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx.type === "deposit" ? "bg-sky-100" : "bg-emerald-100"
                      }`}>
                      {tx.type === "deposit" ? (
                        <ArrowDownToLine className="h-4 w-4 text-sky-600" />
                      ) : (
                        <ArrowUpFromLine className="h-4 w-4 text-emerald-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{tx.merchant?.name || tx.merchant_code || "N/A"}</p>
                      <p className="text-xs text-slate-500">
                        {tx.createdAt ? format(new Date(tx.createdAt), "HH:mm") : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${tx.type === "deposit" ? "text-sky-600" : "text-emerald-600"}`}>
                        RS{Number(tx.amount || 0).toLocaleString()}
                      </p>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">{t("quickActions")}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Link to={createPageUrl("EngineMerchants")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Building2 className="h-5 w-5 text-sky-600" />
                <span className="text-sm">{t("merchants")}</span>
              </Button>
            </Link>
            <Link to={createPageUrl("EngineResellers")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                <span className="text-sm">{t("resellers")}</span>
              </Button>
            </Link>
            <Link to={createPageUrl("EngineTransactions")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                <span className="text-sm">{t("transactions")}</span>
              </Button>
            </Link>
            <Link to={createPageUrl("EngineRouting")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <span className="text-sm">{t("routing")}</span>
              </Button>
            </Link>
            <Link to={createPageUrl("EngineProviders")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Server className="h-5 w-5 text-amber-600" />
                <span className="text-sm">{t("providers")}</span>
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </EngineLayout>
  );
}